import { tv, type VariantProps } from "@ui/lib/tv";
import { Button } from "@ui/primitives/Button";
import type { ReactNode } from "react";

// The Figma "Card / When" item. Each `state` owns its full treatment (fill,
// ring, text) so future availability states (waitlist red / full gray) drop
// in beside `green` without fighting the base classes.
const availabilityItem = tv({
  slots: {
    root: "flex w-full flex-col items-start rounded-xl px-4 py-3 text-left",
    title: "w-full font-extrabold text-xl leading-7",
    body: "w-full text-base leading-6",
  },
  variants: {
    state: {
      // Empty input slot (unpicked check-in/out): white with a neutral hairline.
      default: {
        root: "bg-white shadow-[inset_0_-1px_0_0_var(--color-neutral-300),inset_0_0_0_1px_var(--color-neutral-200)]",
        title: "text-neutral-700",
        body: "text-neutral-600",
      },
      // Unknown-availability session chip: tinted so it sits on the white card
      // instead of outlining against it (the Figma "Fully Booked" surface with
      // readable text).
      neutral: {
        root: "bg-neutral-25 shadow-[inset_0_-1px_0_0_var(--color-neutral-200),inset_0_0_0_1px_var(--color-neutral-100)]",
        title: "text-neutral-700",
        body: "text-neutral-600",
      },
      // Positive/filled: the Figma green treatment (filled check-in slot;
      // later also the "available" session state).
      green: {
        root: "bg-green-50 shadow-[inset_0_-1px_0_0_var(--color-green-200),inset_0_0_0_1px_var(--color-green-100)]",
        title: "text-green-900",
        body: "text-green-800",
      },
    },
    interactive: {
      true: {
        root: [
          "cursor-pointer transition-colors hover:bg-neutral-50",
          "outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 focus-visible:ring-offset-2",
        ],
      },
      false: {},
    },
  },
  defaultVariants: { state: "default", interactive: false },
});

type AvailabilityItemVariants = VariantProps<typeof availabilityItem>;

export interface AvailabilityItemProps {
  title: ReactNode;
  body?: ReactNode;
  /** Renders the whole item as a link when set. */
  href?: string;
  state?: AvailabilityItemVariants["state"];
  className?: string;
}

export function AvailabilityItem({ title, body, href, state = "default", className }: AvailabilityItemProps) {
  const styles = availabilityItem({ state, interactive: href !== undefined });
  const content = (
    <>
      <span className={styles.title()}>{title}</span>
      {body && <span className={styles.body()}>{body}</span>}
    </>
  );

  if (href !== undefined) {
    return (
      <Button href={href} className={styles.root({ className })}>
        {content}
      </Button>
    );
  }
  return <div className={styles.root({ className })}>{content}</div>;
}
